import ArrowLeftIcon from '../../../atoms/icons/arrowLeft';
import ArrowRightIcon from '../../../atoms/icons/arrowRight';
import CircularList from '../circularList/circularList';
import Direction from '../../../../../shared/types/enums/direction';
import Focus from '../../../../../shared/types/enums/focus';
import Main from '../../../../../shared/types/enums/main';
import MainContext from '../../../../../shared/components/utils/context/mainContext';
import SliderArrowLeftContainer from '../../../atoms/container/sliderArrowLeft';
import SliderArrowRightContainer from '../../../atoms/container/sliderArrowRight';
import SliderItem from '../../../elements/sliders/sliderItem';
import SliderListCardsContainer from '../../../atoms/container/sliderListCardsContainer';
import SliderListContainer from '../../../atoms/container/sliderListContainer';
import SliderListFocus from '../../../atoms/focus/sliderListFocus';
import goToContent from '../../../../../shared/actions/content/goToContent';
import useInterval from '../../../../../shared/components/utils/hooks/useInterval';
import useTransform from '../../../../../shared/components/utils/hooks/useTransform';
import { focusSelector } from '../../../../../shared/store/slices/focus.slice';
import { magicRemoteSelector } from '../../../../../shared/store/slices/magicRemote.slice';
import { routerSelector } from '../../../../../shared/store/slices/router.slice';
import { useAppSelector } from '../../../../../shared/store/hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import AdUtils from '@shared/utils/ads';

/** Horizontal offset in pixels between slider items */
const ITEM_OFFSET = 773;
/** Minimum number of items required before enabling loop functionality */
const ITEM_AMOUNT_TO_LOOP = 2;

/**
 * SliderList component that displays a horizontal list of slider items.
 * Supports circular navigation, keyboard/mouse controls, and ad impression tracking.
 *
 * @param {Object} props - Component props
 * @param {number} [props.forcedIndex] - Optional index to force the slider to navigate to
 * @returns {JSX.Element | null} The slider list component or null if no sliders are available
 */
function SliderList({ forcedIndex }: { forcedIndex?: number }) {
  const {
    complete,
    display,
    sliders,
    onChangeElement,
    onItemClick,
    outOfBoundsAction,
  } = useContext(MainContext);

  const [slidersState, setSlidersState] = useState(sliders || []);
  const { enabled, hoveringElement } = useAppSelector(magicRemoteSelector);
  const { focus } = useAppSelector(focusSelector);
  const {
    params: { current: currentParams },
  } = useAppSelector(routerSelector);

  const originalSlidersRef = useRef(sliders || []);
  originalSlidersRef.current = sliders || [];

  const indexRef = useRef(
    (currentParams as { previousFocus: number })?.previousFocus ===
      Focus.SLIDERS
      ? (currentParams as { horizontalIndex: number }).horizontalIndex %
          originalSlidersRef.current.length
      : 0
  );

  const [opacity, setOpacity] = useState(false);
  const [style, setStyle, resetStyle] = useTransform(
    indexRef.current,
    ITEM_OFFSET,
    Direction.HORIZONTAL
  );

  // Actions

  /**
   * Resets the slider list to its initial state.
   * Clears the state, resets the transform style, and restores the original sliders.
   */
  const resetList = () => {
    setSlidersState([]);
    setTimeout(() => {
      resetStyle();
      indexRef.current = 0;
      setSlidersState(originalSlidersRef.current);
    });
  };

  /**
   * Updates the slider list by adding a duplicate item at the specified index.
   * Used for creating seamless circular scrolling.
   *
   * @param {number} index - The index to duplicate from the original sliders array
   */
  const updateItems = (index: number) => {
    setSlidersState((prevSlidersState) => [
      ...prevSlidersState,
      prevSlidersState[index % originalSlidersRef.current.length],
    ]);
  };

  // Event handlers

  /**
   * Handles slider index changes.
   * Updates the current index, sends ad impressions, updates the transform style, and notifies parent components.
   *
   * @param {number} index - The new slider index
   * @param {number} [increment=1] - The increment value for transform calculation
   */
  const onChange = (index: number, increment = 1) => {
    setTimeout(() => {
      indexRef.current = index;
      if (originalSlidersRef.current?.length) {
        const slider = originalSlidersRef.current[index];
        slider?.impressions?.promoFocus.forEach((impression) => {
          AdUtils.sendImageImpression(impression);
        });
      }
      setStyle(increment);
      onChangeElement(Main.SLIDERS, index);
    });
  };

  // Effect hooks

  useEffect(() => {
    setSlidersState(sliders || []);
  }, [sliders]);

  useEffect(() => {
    if (
      originalSlidersRef.current.length &&
      originalSlidersRef.current.length > ITEM_AMOUNT_TO_LOOP &&
      slidersState.length
    ) {
      const index = originalSlidersRef.current.length;
      updateItems(index);
      updateItems(index + 1);
    }
  }, []);

  useEffect(() => {
    setOpacity(
      display?.background || !display?.sliders
        ? false
        : !enabled || (enabled && !hoveringElement)
    );
  }, [display, enabled, hoveringElement]);

  const setListIndex = useRef<(newIndex: number, increment?: number) => void>(
    () => null
  );

  useEffect(() => {
    if (forcedIndex) setListIndex.current(forcedIndex);
  }, [forcedIndex]);

  const [interval, cancelInterval] = useInterval(200);

  return sliders?.length ? (
    <SliderListContainer animation={'slider-opacity'} elOpacity={opacity}>
      <SliderListCardsContainer animation={'slider-scroll'} style={style}>
        {slidersState.length ? (
          <CircularList
            focus={Focus.SLIDERS}
            initialIndex={indexRef.current}
            onChange={onChange}
            originalChildren={originalSlidersRef}
            outOfBounds={outOfBoundsAction}
            setListIndex={setListIndex}
            updateItems={updateItems}
            resetList={resetList}
          >
            {slidersState.map((item, index) => (
              <SliderItem
                onChange={onChange}
                key={index}
                image={item?.img}
                index={index}
                onClick={() => {
                  if (display?.sliders) {
                    onItemClick && onItemClick(index, 0, Focus.SLIDERS);
                    goToContent(item);
                  }
                }}
                releaseAction={() => {
                  goToContent(item);
                }}
              />
            ))}
          </CircularList>
        ) : null}
      </SliderListCardsContainer>
      {/* Focus */}
      {complete && focus === Focus.SLIDERS && !enabled && <SliderListFocus />}
      {/* Arrow right */}
      {enabled && (
        <SliderArrowRightContainer
          animation={'opacity-transition'}
          onMouseEnter={() => interval(() => setListIndex.current(0, 1))}
          onMouseLeave={cancelInterval}
        >
          <ArrowRightIcon width={60} height={100} color={'#FFFFFF'} />
        </SliderArrowRightContainer>
      )}
      {/* Arrow left */}
      {enabled && (
        <SliderArrowLeftContainer
          animation={'opacity-transition'}
          onMouseEnter={() => interval(() => setListIndex.current(0, -1))}
          onMouseLeave={cancelInterval}
        >
          <ArrowLeftIcon width={60} height={100} color={'#FFFFFF'} />
        </SliderArrowLeftContainer>
      )}
    </SliderListContainer>
  ) : null;
}

export default SliderList;
